import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaArrowLeft, FaPlusSquare } from 'react-icons/fa'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { fetchPaymentMethods } from '../../store/reducers/paymentReducer'
import { submitBid } from '../../store/reducers/bidSlice'
import { fetchIntentToken } from '../../store/reducers/payoutReducer'   
import type { RootState, AppDispatch } from '../../store/store'
import './BidReview.css'

interface LocationState {
  buildingName: string
  price: number
  salesTax: number
  processingFee: number
  propertyId: string
  lat: number
  lng: number
}

export default function BidReview() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const raw = useLocation().state as Partial<LocationState> | undefined
const data: LocationState = {
  buildingName: raw?.buildingName ?? '',
  price: Number(raw?.price) || 0,
  salesTax: Number(raw?.salesTax) || 0,
  processingFee: Number(raw?.processingFee) || 0,
  propertyId: raw?.propertyId ?? '',
  lat: Number(raw?.lat) || 0,
  lng: Number(raw?.lng) || 0,
}

  const [showConfirm, setShowConfirm] = useState(false)
  const [bidPrice, setBidPrice] = useState<number>(data.price)
  const [bidError, setBidError] = useState<string | null>(null)
  const [cardError, setCardError] = useState<string | null>(null)

  const total = bidPrice + data.salesTax + data.processingFee

  useEffect(() => {
    if (!data.buildingName) navigate(-1)
  }, [data, navigate])

  useEffect(() => {
    dispatch(fetchPaymentMethods())
  }, [dispatch])

  const { cardList, isFetching, fetchError } = useAppSelector(
    (s: RootState) => s.payment
  )
  const defaultCard = cardList.find(c => c.isDefault)

  useEffect(() => {
    setBidPrice(data.price)
  }, [data.price])

  const handleContinue = () => {
    if (bidPrice <= data.price) {
      setBidError(
        `Your bid must be higher than the base price of $${data.price.toFixed(2)}`
      )
      return
    }
    if (!defaultCard) {
      setCardError('Please add a card first.')
      return
    }
    setShowConfirm(true)
  }

  const handleBuy = () => {
  setShowConfirm(false)
  if (!defaultCard) return

  const amountInCents = Math.round(total * 100)

  dispatch(fetchIntentToken({
    amount: amountInCents,
    currency: 'usd',
    propertyId:data.propertyId   
  }))
    .unwrap()
    .then(({ clientSecret, paymentIntentId, currency }) => {
      return dispatch(submitBid({
        propertyId: data.propertyId,
        bidAmount:        bidPrice,
        paymentMethodId:  defaultCard.id,
        isApplePay:       false,
        paymentIntentId,               
      })).unwrap()
    })
    .then(() => {
      alert('Bid placed successfully')
      navigate(-1)
    })
    .catch(err => {
      alert(`Purchase failed: ${err}`)
    })
}

  return (
    <>
      <div className="order-container">
        <div className="order-header">
          <FaArrowLeft
            className="order-back-icon"
            onClick={() => navigate(-1)}
          />
          <h2 className="order-screen-title">Order Review</h2>
          <button
            className="header-add-card-btn"
            onClick={() => navigate('/Add-Card')}
          >
            <FaPlusSquare className="add-icon" />
            Add Card
          </button>
        </div>

        <h1 className="order-title">{data.buildingName}</h1>

        <div className="order-section">
          <div className="order-row">
            <label className="label" htmlFor="bidPrice">
              Bid Price
            </label>
            <div className="input-wrapper">
              <span className="input-prefix">$</span>
              <input
                type="number"
                id="bidPrice"
                className="bid-input"
                value={bidPrice}
                onChange={e => {
                  setBidPrice(Number(e.target.value))
                  setBidError(null)
                }}
                min={data.price + 0.01}
                step="0.01"
              />
            </div>
          </div>
          {bidError && <div className="bid-error-text">{bidError}</div>}

          <div className="order-row">
            <span className="label">Sales Tax</span>
            <span className="value">
              ${data.salesTax.toFixed(2)}
            </span>
          </div>

          <div className="order-row">
            <span className="label">Processing Fee</span>
            <span className="value">
              ${data.processingFee.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="divider" />

        <div className="order-section total-section">
          <p className="order-note">
            *All applicable taxes are included in price.
          </p>
          <div className="order-row">
            <strong className="label">Total (incl. taxes)*</strong>
            <strong className="value">
              ${total.toFixed(2)}
            </strong>
          </div>
        </div>

        <div className="divider" />

        <div className="order-section">
          {isFetching ? (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value">Loading…</span>
            </div>
          ) : fetchError ? (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value error">{fetchError}</span>
            </div>
          ) : defaultCard ? (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value">
                {defaultCard.type} •••• {defaultCard.last4}
              </span>
            </div>
          ) : (
            <div className="order-row">
              <span className="label">
                Payment Method
              </span>
              <span className="value">
                No card on file
              </span>
            </div>
          )}
          {cardError && <div className="bid-error-text">{cardError}</div>}
        </div>

        <div className="divider" />

        <button
          className="continue-btn"
          onClick={handleContinue}
        >
          Continue to Pay
        </button>
      </div>

      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Purchase</h3>
            <p>
              Are you sure you want to bid on{' '}
              <strong>{data.buildingName}</strong> with{' '}
              <strong>${bidPrice.toFixed(2)}</strong>?
            </p>
            <div className="modal-actions">
              <button
                className="modal-btn cancel"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="modal-btn buy"
                onClick={handleBuy}
              >
                Buy
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
